"use client";

import React, { useState, useEffect, Suspense } from "react";
import Image from "next/image";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { 
  AtSymbolIcon, 
  EyeIcon, 
  EyeSlashIcon,
  ExclamationCircleIcon 
} from "@heroicons/react/24/outline";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useAuthentication } from "@/hooks/useAuthentication";
import { apiClient } from "@/lib/api"; 
import { toast } from "sonner"; 
import { useLocale } from "@/context/LocaleContext";

interface FormErrors {
  email?: string;
  password?: string;
}

const MAX_ATTEMPTS = 3;          
const BLOCK_DURATION_SECONDS = 30; 
const SUBMIT_COOLDOWN_SECONDS = 3;  

function LoginContent() {
  const { t, locale } = useLocale();
  const router = useRouter();
  const searchParams = useSearchParams();
  usePageTitle(t("Login.login_btn") || "Login");
  
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false); 
  const [googleErrorMessage, setGoogleErrorMessage] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  const [failedAttempts, setFailedAttempts] = useState(0);
  const [cooldown, setCooldown] = useState(0);

  const { loginUser, loginWithGoogle, isLoading, error: serverError } = useAuthentication();

  useEffect(() => {
    if (cooldown <= 0) return;

    const timer = setInterval(() => {
      setCooldown((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [cooldown]);

  useEffect(() => {
    const errorParam = searchParams.get("error");
    if (errorParam) {
      if (
        errorParam === "no_code" || 
        errorParam === "cancelled" || 
        errorParam === "google_cancelled"
      ) {
        const fallbackMsg = "Proses login dengan Google dibatalkan atau gagal mendapatkan profil.";
        const translatedMsg = t("Login.google_cancelled");
        
        setGoogleErrorMessage(
          translatedMsg && translatedMsg !== "Login.google_cancelled" 
            ? translatedMsg 
            : fallbackMsg
        );
      } else {
        setGoogleErrorMessage(errorParam);
      }
    }
  }, [searchParams, t]);

  const validateForm = (): boolean => {
    const errors: FormErrors = {};
    let isValid = true;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim()) {
      errors.email = t("Login.email_required") || "Masukkan email terlebih dahulu...";
      isValid = false;
    } else if (!emailRegex.test(email)) {
      errors.email = t("Login.email_invalid") || "Format email tidak valid";
      isValid = false;
    }

    if (!password) {
      errors.password = t("Login.password_required") || "Masukkan password terlebih dahulu...";
      isValid = false;
    } 

    setFormErrors(errors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (cooldown > 0) {
      toast.error(`Terlalu banyak percobaan. Silakan tunggu ${cooldown} detik.`);
      return;
    }

    if (!validateForm()) return;

    setCooldown(SUBMIT_COOLDOWN_SECONDS);

    const cleanEmail = email.trim();
    const res = await loginUser({ email: cleanEmail, password });
    
    if (res.success) {
      setFailedAttempts(0);
      setCooldown(0);

      toast.success(res.data?.message || t("Login.welcome_back_toast") || "Welcome back!");
      
      const targetAdminPath = locale === "en" ? "/en/admin" : "/admin";
      window.location.href = targetAdminPath;
    } else {
      const errorMessage = res.error || "";
      const lowerError = errorMessage.toLowerCase();

      // Tangkap kondisi email belum diverifikasi
      if (
        lowerError.includes("verifikasi") || 
        lowerError.includes("verify") || 
        lowerError.includes("unverified")
      ) {
        toast.info("Email belum diverifikasi. Mengarahkan ke halaman verifikasi OTP...");

        // Kirim kode OTP baru via background request
        try {
          await apiClient.post(`/auth/send-otp?email=${encodeURIComponent(cleanEmail)}`);
        } catch (err) {
          console.warn("Auto resend OTP on unverified login warning:", err);
        }

        const targetOtpPath = locale === "en"
          ? `/en/submit-otp?email=${encodeURIComponent(cleanEmail)}`
          : `/submit-otp?email=${encodeURIComponent(cleanEmail)}`;
          
        // Delay singkat agar transisi tidak mendadak dan user sempat melihat feedback toast
        setTimeout(() => {
          router.push(targetOtpPath);
        }, 600);
        return;
      }

      // Error autentikasi biasa (password keliru, dll)
      toast.error(errorMessage || t("Login.login_failed_toast") || "Login failed");
      
      const nextAttempts = failedAttempts + 1;
      setFailedAttempts(nextAttempts);

      if (nextAttempts >= MAX_ATTEMPTS) {
        setCooldown(BLOCK_DURATION_SECONDS);
        setFailedAttempts(0); 
        toast.error(`Batas percobaan login tercapai. Coba lagi dalam ${BLOCK_DURATION_SECONDS} detik.`);
      }
    }
  };

  const handleGoogleLogin = async () => {
    if (cooldown > 0) {
      toast.error(`Silakan tunggu ${cooldown} detik sebelum mencoba kembali.`);
      return;
    }

    setIsGoogleLoading(true);
    setGoogleErrorMessage(null);

    try {
      const result = await loginWithGoogle();

      if (result.success && result.url) {
        const googleUrl = new URL(result.url);
        
        if (!googleUrl.searchParams.has("prompt")) {
          googleUrl.searchParams.append("prompt", "select_account");
        }

        window.location.href = googleUrl.toString();
      } else {
        setIsGoogleLoading(false);
        setGoogleErrorMessage(
          result.error || 
          t("Login.oauth_error") || 
          "Unable to get profile information from Google"
        );
      }
    } catch (error) {
      setIsGoogleLoading(false);
      setGoogleErrorMessage(
        t("Login.server_error") || 
        "Gagal menghubungkan ke server."
      );
      console.error("Google login error:", error);
    }
  };

  const clearError = (field: keyof FormErrors) => {
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const isSubmitDisabled = isLoading || isGoogleLoading || cooldown > 0;

  return (
    <div className="min-h-screen bg-[#fafafa] flex items-center justify-center p-4 font-sans relative select-none">
      <div className="w-full max-w-[900px] flex justify-center lg:justify-between items-center lg:items-stretch gap-0 lg:gap-8">
        
        {/* HERO LEFT SIDEBAR */}
        <div className="hidden lg:flex w-[48%] bg-gradient-to-b from-[#F15C61] to-[#f47c80] rounded-[28px] p-8 pb-0 flex-col justify-between relative overflow-hidden min-h-[480px] shadow-[0_20px_50px_rgba(241,92,97,0.2)]">    
          <div className="relative z-10 mt-1 pr-2 lg:pr-4"> 
            <h1 className="text-white text-[1.75rem] font-bold leading-[1.25] tracking-tight mb-3 text-balance">
              {t("Login.hero_title") || "Undangan Digital Profesional No.1 Di Indonesia. Untuk Segala Jenis Acara 100% GRATIS"}
            </h1>
            <p className="text-white/95 text-[0.9rem] leading-relaxed font-medium">
              {t("Login.hero_desc") || "Kelola undangan, RSVP, pengingat acara, hingga amplop digital dalam satu platform yang rapi, otomatis, dan gratis tanpa batas."}
            </p>
          </div>

          <div className="relative mt-6 flex-1 w-full flex justify-center items-end">
            <div className="relative w-[290px] aspect-[4/5]">
              <div className="absolute left-1/2 bottom-[5%] -translate-x-1/2 w-[90%] h-[60%] bg-white/40 blur-[70px] rounded-full -z-10 pointer-events-none"></div>
              <Image 
                src="/images/cta/cta.png" 
                alt="Elynk App Mockup" 
                fill 
                className="object-contain object-bottom drop-shadow-[0_20px_40px_rgba(0,0,0,0.15)] relative z-10"
                priority
              />
            </div>
          </div>
        </div>

        {/* RIGHT LOGIN FORM */}
        <div className="w-full max-w-[400px] lg:w-[52%] lg:max-w-[380px] bg-white lg:bg-transparent shadow-sm lg:shadow-none rounded-3xl lg:rounded-none flex flex-col justify-center py-8 lg:py-4 px-6 lg:px-0 lg:pr-2">
          
          <div className="flex flex-col items-center mb-6">
            <Link href="/" className="mb-4 inline-block">
              <Image 
                src="/images/logo/elynk.png"
                alt="Elynk Logo" 
                width={120} 
                height={38} 
                className="object-contain"
              />
            </Link>
            <h2 className="text-[1.5rem] lg:text-[1.7rem] font-bold text-[#2A2A2A] text-center mb-1 tracking-tight">
              {t("Login.welcome_back") || "Welcome Back"}
            </h2>
            <p className="text-[0.85rem] text-gray-400 font-medium">
              {t("Login.please_login") || "Please login to your account"}
            </p>
          </div>

          {/* WARNING RATE LIMIT */}
          {cooldown > 0 && cooldown > SUBMIT_COOLDOWN_SECONDS && (
            <div className="mb-4 bg-amber-50 border border-amber-200 text-amber-800 px-4 py-2.5 rounded-[10px] text-[0.82rem] font-medium text-center shadow-sm flex items-center justify-center gap-2">
              <ExclamationCircleIcon className="w-4 h-4 shrink-0 text-amber-600" />
              <span>Terlalu banyak percobaan. Coba lagi dalam <b>{cooldown}</b> detik.</span>
            </div>
          )}

          {/* BANNER ERROR GOOGLE CANCELLED */}
          {googleErrorMessage && (
            <div className="mb-4 bg-[#A32226] text-white px-4 py-2.5 rounded-[8px] text-[0.85rem] font-semibold text-center shadow-sm flex items-center justify-center gap-2">
              <ExclamationCircleIcon className="w-4 h-4 shrink-0" />
              <span>{googleErrorMessage}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="flex flex-col space-y-3.5" noValidate>
            <div className="flex flex-col space-y-1">
              <label className="text-[0.85rem] font-medium text-[#2A2A2A]">{t("Login.email_label") || "Email address"}</label>
              <div className="relative">
                <input 
                  type="email" 
                  disabled={cooldown > SUBMIT_COOLDOWN_SECONDS}
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); clearError("email"); }}
                  placeholder={t("Login.email_placeholder") || "name@domain.com"}
                  className={`w-full text-[#2A2A2A] placeholder-gray-400 rounded-[10px] h-[42px] px-3.5 outline-none transition-all text-[0.85rem]
                    ${formErrors.email 
                      ? 'bg-red-50 border border-red-400 focus:ring-2 focus:ring-red-400/30' 
                      : 'bg-[#F5F5F5] border-none focus:ring-2 focus:ring-[#F15C61]/30'}
                    ${cooldown > SUBMIT_COOLDOWN_SECONDS ? 'opacity-60 cursor-not-allowed' : ''}`}
                />
                <AtSymbolIcon className={`absolute right-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] ${formErrors.email ? 'text-red-400' : 'text-gray-400'}`} strokeWidth={2} />
              </div>
              {formErrors.email && (
                <span className="flex items-center gap-1 text-red-500 text-[0.75rem] font-medium mt-1 ml-1">
                  <ExclamationCircleIcon className="w-3.5 h-3.5" /> {formErrors.email}
                </span>
              )}
            </div>

            <div className="flex flex-col space-y-1">
              <label className="text-[0.85rem] font-medium text-[#2A2A2A]">{t("Login.password_label") || "Password"}</label>
              <div className="relative">
                <input 
                  type={showPassword ? "text" : "password"} 
                  disabled={cooldown > SUBMIT_COOLDOWN_SECONDS}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); clearError("password"); }}
                  placeholder="**********"
                  className={`w-full text-[#2A2A2A] placeholder-gray-400 rounded-[10px] h-[42px] px-3.5 outline-none transition-all text-[0.85rem] pr-10
                    ${formErrors.password 
                      ? 'bg-red-50 border border-red-400 focus:ring-2 focus:ring-red-400/30' 
                      : 'bg-[#F5F5F5] border-none focus:ring-2 focus:ring-[#F15C61]/30'}
                    ${cooldown > SUBMIT_COOLDOWN_SECONDS ? 'opacity-60 cursor-not-allowed' : ''}`}
                />
                <button 
                  type="button" 
                  onClick={() => setShowPassword(!showPassword)}
                  className={`absolute right-3.5 top-1/2 -translate-y-1/2 outline-none transition-colors cursor-pointer
                    ${formErrors.password ? 'text-red-400 hover:text-red-600' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  {showPassword ? <EyeIcon className="w-[18px] h-[18px]" strokeWidth={2} /> : <EyeSlashIcon className="w-[18px] h-[18px]" strokeWidth={2} />}
                </button>
              </div>
              {formErrors.password && (
                <span className="flex items-start gap-1 text-red-500 text-[0.75rem] font-medium mt-1 ml-1 leading-tight">
                  <ExclamationCircleIcon className="w-3.5 h-3.5 shrink-0 mt-0.5" /> <span>{formErrors.password}</span>
                </span>
              )}
            </div>

            <div className="flex justify-end pt-0.5">
              <Link href="/forgot-password" className="text-[0.8rem] text-gray-400 hover:text-[#F15C61] transition-colors font-medium">
                {t("Login.forgot_password") || "Forgot Password ?"}
              </Link>
            </div>

            {/* Kotak error hanya tampil jika BUKAN masalah verifikasi email */}
            {serverError && 
             !serverError.toLowerCase().includes("verif") && 
             !serverError.toLowerCase().includes("unverified") && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-3.5 py-2.5 rounded-[10px] text-[0.8rem] font-medium flex items-start gap-2 mt-1 animate-in fade-in duration-200">
                 <ExclamationCircleIcon className="w-4 h-4 shrink-0 mt-0.5" />
                 <span>{serverError}</span>
              </div>
            )}

            <div className="pt-1.5">
              <button 
                type="submit" 
                disabled={isSubmitDisabled}
                className="w-full flex items-center justify-center bg-[#F15C61] hover:bg-[#E04D52] disabled:bg-[#f15c61]/70 disabled:cursor-not-allowed text-white font-semibold text-[0.95rem] h-[42px] rounded-[10px] transition-colors shadow-sm cursor-pointer active:scale-[0.99]"
              >
                {isLoading ? (
                   <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                     <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                     <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                   </svg>
                ) : null}
                {cooldown > 0 
                  ? `Silakan tunggu (${cooldown}s)` 
                  : isLoading 
                    ? (t("Login.logging_in") || "Logging in...") 
                    : (t("Login.login_btn") || "Login")}
              </button>
            </div>
          </form>

          <div className="flex items-center justify-center gap-3 my-5">
            <hr className="w-full border-gray-200" />
            <span className="text-[0.75rem] text-gray-400 whitespace-nowrap px-1">{t("Login.or_login_with") || "Or Login With"}</span>
            <hr className="w-full border-gray-200" />
          </div>

          {/* GOOGLE BUTTON */}
          <button 
            type="button" 
            onClick={handleGoogleLogin}
            disabled={isSubmitDisabled}
            className="w-full flex items-center justify-center gap-2.5 h-[42px] text-[0.9rem] font-semibold text-[#2A2A2A] transition-colors bg-white border border-gray-200 rounded-[10px] hover:bg-gray-50 shadow-sm disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed cursor-pointer active:scale-[0.99]"
          >
            {isGoogleLoading ? (
              <svg className="animate-spin h-4 w-4 text-[#2A2A2A]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18.7511 10.1944C18.7511 9.47495 18.6915 8.94995 18.5626 8.40552H10.1797V11.6527H15.1003C15.0011 12.4597 14.4654 13.675 13.2749 14.4916L13.2582 14.6003L15.9087 16.6126L16.0924 16.6305C17.7788 15.1041 18.7511 12.8583 18.7511 10.1944Z" fill="#4285F4" />
                <path d="M10.1788 18.75C12.5895 18.75 14.6133 17.9722 16.0915 16.6305L13.274 14.4916C12.5201 15.0068 11.5081 15.3666 10.1788 15.3666C7.81773 15.3666 5.81379 13.8402 5.09944 11.7305L4.99473 11.7392L2.23868 13.8295L2.20264 13.9277C3.67087 16.786 6.68674 18.75 10.1788 18.75Z" fill="#34A853" />
                <path d="M5.10014 11.7305C4.91165 11.186 4.80257 10.6027 4.80257 9.99992C4.80257 9.3971 4.91165 8.81379 5.09022 8.26935L5.08523 8.1534L2.29464 6.02954L2.20333 6.0721C1.5982 7.25823 1.25098 8.5902 1.25098 9.99992C1.25098 11.4096 1.5982 12.7415 2.20333 13.9277L5.10014 11.7305Z" fill="#FBBC05" />
                <path d="M10.1789 4.63331C11.8554 4.63331 12.9864 5.34303 13.6312 5.93612L16.1511 3.525C14.6035 2.11528 12.5895 1.25 10.1789 1.25C6.68676 1.25 3.67088 3.21387 2.20264 6.07218L5.08953 8.26943C5.81381 6.15972 7.81776 4.63331 10.1789 4.63331Z" fill="#EB4335" />
              </svg>
            )}
            {isGoogleLoading ? (t("Login.redirecting") || "Mengarahkan...") : (t("Login.google_btn") || "Google")}
          </button>

          <div className="mt-6 flex flex-col items-center justify-center gap-2 text-[0.85rem] font-medium">
            <div className="text-gray-600">
              {t("Login.no_account") || "Don't have account?"}{" "}
              <Link href="/register" className="font-bold text-[#F15C61] hover:underline transition-colors">
                {t("Login.register_link") || "Register"}
              </Link>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default function LoginClient() {
  return (
    <Suspense fallback={
      <div className="min-h-screen bg-[#fafafa] flex items-center justify-center">
        <span className="text-gray-400 text-sm font-medium">Loading...</span>
      </div>
    }>
      <LoginContent />
    </Suspense>
  );
}